// Lineas de WhatsApp Web (whatsapp-web.js): conductores y clientes del bot.
// Desde aqui se escanea el QR de cada numero, se ve si esta conectado y se prueba un envio.
// Todo pasa por /api/whatsapp-servicio del servidor del panel, que guarda el token del
// servicio y exige la sesion de Firebase.
import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from '../ThemeContext';
import { cabeceraSesion } from '../utils/sesionPanel';

const ESTADOS = {
  apagada: { texto: 'Apagada', color: '#6b7280' },
  iniciando: { texto: 'Iniciando…', color: '#f59e0b' },
  'esperando-qr': { texto: 'Esperando que escanees el QR', color: '#f59e0b' },
  conectada: { texto: 'Conectada', color: '#10b981' },
  'con-error': { texto: 'Con error', color: '#ef4444' }
};

const servicio = async (ruta, metodo = 'GET', cuerpo) => {
  const respuesta = await fetch(`/api/whatsapp-servicio?ruta=${encodeURIComponent(ruta)}`, {
    method: metodo,
    headers: { 'content-type': 'application/json', ...(await cabeceraSesion()) },
    body: cuerpo ? JSON.stringify(cuerpo) : undefined
  });
  let datos = {};
  try { datos = await respuesta.json(); } catch (e) { /* respuesta vacia */ }
  if (!respuesta.ok) throw new Error(datos.message || `Error ${respuesta.status}`);
  return datos;
};

function TarjetaSesion({ sesion, onCambio }) {
  const { colors } = useTheme();
  const [qr, setQr] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [para, setPara] = useState('');
  const [mensaje, setMensaje] = useState('Mensaje de prueba desde el panel de Plaza del Valle');
  const [eventos, setEventos] = useState([]);
  const [verEventos, setVerEventos] = useState(false);

  const estado = ESTADOS[sesion.estado] || { texto: sesion.estado, color: '#6b7280' };

  // El QR cambia cada ~20 s mientras nadie lo escanea: se vuelve a pedir en cada refresco.
  useEffect(() => {
    let vigente = true;
    if (sesion.estado === 'esperando-qr' && sesion.hayQr) {
      servicio(`/sesiones/${sesion.id}/qr`).then(d => { if (vigente) setQr(d.qr); }).catch(() => {});
    } else {
      setQr(null);
    }
    return () => { vigente = false; };
  }, [sesion.id, sesion.estado, sesion.hayQr, sesion.qrGeneradoEn]);

  useEffect(() => {
    if (!verEventos) return undefined;
    let vigente = true;
    const cargar = () => servicio(`/sesiones/${sesion.id}/eventos?limite=30`)
      .then(d => { if (vigente) setEventos(d.eventos || []); }).catch(() => {});
    cargar();
    const intervalo = setInterval(cargar, 5000);
    return () => { vigente = false; clearInterval(intervalo); };
  }, [verEventos, sesion.id]);

  const accion = async (nombre, confirmar) => {
    if (confirmar && !window.confirm(confirmar)) return;
    setOcupado(true);
    setAviso(null);
    try {
      await servicio(`/sesiones/${sesion.id}/${nombre}`, 'POST', {});
      onCambio();
    } catch (error) {
      setAviso({ ok: false, texto: error.message });
    } finally {
      setOcupado(false);
    }
  };

  const enviarPrueba = async (e) => {
    e.preventDefault();
    setOcupado(true);
    setAviso(null);
    try {
      const d = await servicio(`/sesiones/${sesion.id}/mensajes`, 'POST', { para, mensaje });
      setAviso({ ok: true, texto: `Enviado a ${String(d.enviado?.para || para).replace(/@.*$/, '')}` });
      onCambio();
    } catch (error) {
      setAviso({ ok: false, texto: error.message });
    } finally {
      setOcupado(false);
    }
  };

  const boton = (fondo) => ({
    padding: '8px 14px', borderRadius: 6, border: 'none', cursor: ocupado ? 'wait' : 'pointer',
    background: fondo, color: '#fff', fontWeight: 600, fontSize: 13, opacity: ocupado ? 0.6 : 1
  });

  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 20, flex: '1 1 380px', minWidth: 320 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, color: colors.text }}>{sesion.nombre}</h3>
          <p style={{ margin: '4px 0 0', color: colors.textSecondary, fontSize: 13 }}>{sesion.descripcion}</p>
        </div>
        <span style={{ background: estado.color, color: '#fff', borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
          {estado.texto}
        </span>
      </div>

      <div style={{ marginTop: 14, fontSize: 14, color: colors.text, lineHeight: 1.7 }}>
        <div><b>Número:</b> {sesion.numero ? `+${sesion.numero}` : '—'}{sesion.nombreWhatsapp ? ` (${sesion.nombreWhatsapp})` : ''}</div>
        <div><b>Conectada desde:</b> {sesion.conectadaDesde ? new Date(sesion.conectadaDesde).toLocaleString('es-EC') : '—'}</div>
        <div><b>Enviados / recibidos:</b> {sesion.enviados} / {sesion.recibidos} <span style={{ color: colors.textSecondary }}>(desde el último arranque del servicio)</span></div>
        {sesion.webhook?.url ? <div><b>Webhook:</b> {sesion.webhook.activo ? 'activo' : 'apagado'}</div> : null}
        {sesion.ultimoError ? <div style={{ color: '#ef4444' }}><b>Último error:</b> {sesion.ultimoError}</div> : null}
      </div>

      {sesion.estado === 'esperando-qr' && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          {qr ? (
            <img src={qr} alt={`QR de ${sesion.nombre}`} style={{ width: 280, height: 280, background: '#fff', padding: 8, borderRadius: 8 }} />
          ) : (
            <div style={{ color: colors.textSecondary }}>Generando QR…</div>
          )}
          <p style={{ fontSize: 13, color: colors.textSecondary, margin: '8px 0 0' }}>
            En el teléfono de esta línea: WhatsApp → Dispositivos vinculados → Vincular un dispositivo.
          </p>
        </div>
      )}
      {sesion.estado === 'iniciando' && (
        <p style={{ marginTop: 16, color: colors.textSecondary, textAlign: 'center' }}>Abriendo WhatsApp Web, el QR aparece en unos segundos…</p>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
        {(sesion.estado === 'apagada' || sesion.estado === 'con-error') && (
          <button style={boton('#10b981')} disabled={ocupado} onClick={() => accion('iniciar')}>
            {sesion.numero ? 'Conectar' : 'Generar QR'}
          </button>
        )}
        {sesion.estado !== 'apagada' && (
          <button style={boton('#3b82f6')} disabled={ocupado} onClick={() => accion('reiniciar')}>Reiniciar</button>
        )}
        {sesion.estado !== 'apagada' && (
          <button style={boton('#6b7280')} disabled={ocupado} onClick={() => accion('detener', 'Se apaga la línea (no se pierde la vinculación). ¿Continuar?')}>Apagar</button>
        )}
        <button style={boton('#ef4444')} disabled={ocupado}
          onClick={() => accion('cerrar', `Se desvincula el número de "${sesion.nombre}". Para volver a usarlo habrá que escanear un QR nuevo. ¿Continuar?`)}>
          Desvincular número
        </button>
      </div>

      {sesion.estado === 'conectada' && (
        <form onSubmit={enviarPrueba} style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <b style={{ color: colors.text, fontSize: 14 }}>Enviar mensaje de prueba</b>
          <input value={para} onChange={e => setPara(e.target.value)} placeholder="Número (0981234567)" required
            style={{ padding: 8, borderRadius: 6, border: `1px solid ${colors.border}`, background: colors.background, color: colors.text }} />
          <textarea value={mensaje} onChange={e => setMensaje(e.target.value)} rows={2} required
            style={{ padding: 8, borderRadius: 6, border: `1px solid ${colors.border}`, background: colors.background, color: colors.text }} />
          {sesion.id === 'clientes' && (
            <span style={{ fontSize: 12, color: colors.textSecondary }}>
              Por seguridad, la línea de clientes solo escribe a quien le escribió en las últimas 23 horas.
            </span>
          )}
          <button type="submit" style={{ ...boton(colors.primary), alignSelf: 'flex-start' }} disabled={ocupado}>Enviar</button>
        </form>
      )}

      {aviso && (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 6, fontSize: 13, background: aviso.ok ? '#d1fae5' : '#fee2e2', color: aviso.ok ? '#065f46' : '#991b1b' }}>
          {aviso.texto}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <button type="button" onClick={() => setVerEventos(v => !v)}
          style={{ background: 'none', border: 'none', color: colors.primary, cursor: 'pointer', padding: 0, fontSize: 13 }}>
          {verEventos ? 'Ocultar actividad' : 'Ver actividad reciente'}
        </button>
        {verEventos && (
          <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 8, fontSize: 12, color: colors.text }}>
            {eventos.length === 0 ? <div style={{ color: colors.textSecondary }}>Sin actividad todavía.</div> : eventos.map((ev, i) => (
              <div key={i} style={{ padding: '4px 0', borderBottom: `1px solid ${colors.border}` }}>
                <span style={{ color: colors.textSecondary }}>{new Date(ev.fecha).toLocaleTimeString('es-EC')}</span>{' '}
                <b>{ev.tipo}</b>{' '}
                {ev.telefono || (ev.de || ev.para || '').replace(/@.*$/, '')} {ev.cuerpo || ev.mensaje || ev.motivo || ''}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function WhatsAppWeb() {
  const { colors } = useTheme();
  const [sesiones, setSesiones] = useState([]);
  const [proveedores, setProveedores] = useState(null);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const d = await servicio('/sesiones');
      setSesiones(d.sesiones || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    cargar();
    fetch('/api/whatsapp-proveedores').then(r => (r.ok ? r.json() : null)).then(setProveedores).catch(() => {});
    // Mientras se espera el QR conviene refrescar seguido; conectado, cada 10 s alcanza.
    const intervalo = setInterval(cargar, 4000);
    return () => clearInterval(intervalo);
  }, [cargar]);

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginTop: 0, color: colors.text }}>WhatsApp Web</h2>
      <p style={{ color: colors.textSecondary, marginTop: 0, maxWidth: 820 }}>
        Las dos líneas de WhatsApp de la empresa. Escanea el QR una sola vez con el teléfono de cada número;
        la vinculación se conserva aunque se reinicie el servidor.
      </p>
      {proveedores && (
        <div style={{ fontSize: 13, color: colors.text, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, maxWidth: 820 }}>
          <b>Proveedor en uso:</b> mensajes a conductores → <b>{proveedores.conductores}</b> · mensajes a clientes → <b>{proveedores.clientes}</b>
        </div>
      )}
      {error && (
        <div style={{ padding: 12, borderRadius: 8, background: '#fee2e2', color: '#991b1b', marginBottom: 16 }}>{error}</div>
      )}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {sesiones.map(s => <TarjetaSesion key={s.id} sesion={s} onCambio={cargar} />)}
      </div>
      <p style={{ fontSize: 12, color: colors.textSecondary, marginTop: 20, maxWidth: 820 }}>
        WhatsApp Web no es la API oficial: el teléfono debe seguir con la cuenta activa y los envíos masivos pueden
        provocar el bloqueo del número.
      </p>
    </div>
  );
}

export default WhatsAppWeb;
